import type { Entity } from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import { Card, CardContent, Typography, makeStyles } from '@material-ui/core';
import { shortRef, str } from '../vocabulary/refs';
import { entityPath } from './homeData';

/**
 * The compact entity card used by every strip on the home page.
 *
 * Deliberately not BrowsePage's card. That one carries a description, a
 * lifecycle chip and a two-column client/team footer, which is the right
 * density when you are scanning a filtered result set and comparing candidates.
 * Here you are re-finding something you already know, so the name does almost
 * all the work and one line of context is enough to tell two similarly named
 * things apart.
 */
const useStyles = makeStyles(theme => ({
  link: { display: 'block', height: '100%', textDecoration: 'none' },
  card: {
    height: '100%',
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'var(--ar-surface-2)',
    },
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    // MUI adds 24px to the last child of a CardContent; at this size that
    // reads as a hole under the text.
    '&:last-child': { paddingBottom: 14 },
    paddingTop: 12,
  },
  kind: {
    fontSize: '0.625rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--ar-fg-3)',
  },
  // Wraps to two lines rather than truncating. A tile is only as wide as the
  // column it lands in, and "Example Node.j…" identifies nothing — the name is
  // the one part of this card that has to survive at every width.
  name: {
    fontSize: '0.875rem',
    fontWeight: 620,
    letterSpacing: '-0.011em',
    color: theme.palette.primary.main,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  sub: {
    fontSize: '0.75rem',
    color: 'var(--ar-fg-2)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export const EntityTile = ({ entity }: { entity: Entity }) => {
  const classes = useStyles();
  const type = str(entity.spec?.type);
  // What it is part of beats who owns it: on a home page the entities are
  // already yours or starred, so the owner is nearly always you.
  const system = shortRef(entity.spec?.system);
  const owner = shortRef(entity.spec?.owner);

  return (
    <Link to={entityPath(entity)} className={classes.link}>
      <Card className={classes.card}>
        <CardContent className={classes.body}>
          <span className={classes.kind}>
            {[entity.kind, type].filter(Boolean).join(' · ')}
          </span>
          <Typography className={classes.name}>
            {entity.metadata.title ?? entity.metadata.name}
          </Typography>
          <span className={classes.sub}>
            {system ? `part of ${system}` : owner || ' '}
          </span>
        </CardContent>
      </Card>
    </Link>
  );
};
